import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import BackspaceOutlinedIcon from '@material-ui/icons/BackspaceOutlined';

/**
 * The on-screen numeric keypad for Receive Mode's unlock code and Picture Mode's
 * order number.
 *
 * Replaces `react-numeric-keyboard`, which was a single-component dependency that
 * dragged in `node-sass` -> `node-gyp` and roughly 55 of this repository's
 * Dependabot alerts, plus the `--ignore-scripts` install workaround needed because
 * node-sass cannot build on Python 3.12+. None of that shipped to the browser, but
 * none of it was earning its place either: the layout is twelve keys.
 *
 * Emits the same key names the previous library did ('0'-'9' and 'Backspace'), so
 * the caller's handler is unchanged and keeps matching physical keyboard events.
 */
const useStyles = makeStyles((theme) => ({
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 6,
        padding: 6,
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: theme.palette.background.default,
    },
    key: {
        // Tablets are the only target, so keys are sized for a fingertip rather
        // than a cursor. The floor keeps them tappable on a short viewport.
        minHeight: 'max(56px, 9vh)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 5,
        boxShadow: theme.shadows[1],
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        fontSize: '1.5rem',
        fontFamily: theme.typography.fontFamily,
        cursor: 'pointer',
        userSelect: 'none',
        padding: 0,
        '&:active': {
            backgroundColor: theme.palette.action.selected,
        },
        '&:disabled': {
            cursor: 'default',
            color: theme.palette.text.disabled,
        },
    },
    // The Done button is supplied by the caller and brings its own styling, so
    // this slot only provides the cell.
    slot: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'max(56px, 9vh)',
        borderRadius: 5,
        overflow: 'hidden',
    },
}));

const NUMBERS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

export default function NumericKeypad(props) {
    const {
        onKey,
        disabled,
        leftSlot,
    } = props;

    const classes = useStyles();

    const key = (name, content, ariaLabel) => (
        <button
            key={name}
            type="button"
            className={classes.key}
            disabled={disabled}
            aria-label={ariaLabel || name}
            onClick={() => onKey(name)}
        >
            {content}
        </button>
    );

    return (
        <div className={classes.grid} data-keypad>
            {NUMBERS.map((n) => key(n, n))}
            <div className={classes.slot}>{leftSlot}</div>
            {key('0', '0')}
            {key('Backspace', <BackspaceOutlinedIcon/>, 'Backspace')}
        </div>
    );
}
